import { AccountUsageLimitDto } from '../../models/account-usage.model';
import { LanguageService } from '../../../../services/language/language.service';

/**
 * Codigos de las 3 metricas clave del plan que se destacan en el HERO,
 * en el orden en que se muestran. Se mantienen separados del LIMIT_ORDER
 * del grid para que la seleccion del hero sea explicita y estable.
 */
export const KEY_METRIC_CODES = [
  'monthly_chat_queries',
  'monthly_llm_tokens',
  'monthly_documents_uploaded',
] as const;

export type HeroMetricState =
  | 'missing'
  | 'unavailable'
  | 'unlimited'
  | 'blocking'
  | 'overage_allowed'
  | 'warning'
  | 'healthy';

export interface HeroMetricView {
  limitCode: string;
  label: string;
  state: HeroMetricState;
  /** false cuando no hay dato fiable (missing o unavailable): sin barra ni %. */
  hasData: boolean;
  consumedDisplay: string;
  limitValueDisplay: string;
  unitLabel: string;
  periodLabel: string;
  displayPercent: number | null;
  progressWidth: number;
}

const LOCALE_MAP: Record<string, string> = {
  es: 'es-CO', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', ja: 'ja-JP',
};

/**
 * Construye exactamente 3 tarjetas (una por metrica clave, en orden).
 * Si una metrica no viene en el snapshot, produce una tarjeta 'missing'
 * en vez de omitirla, para que el owner vea el hueco de datos.
 */
export function buildHeroMetrics(
  limits: AccountUsageLimitDto[],
  languageService: LanguageService
): HeroMetricView[] {
  const byCode = new Map((limits ?? []).map((limit) => [limit.limitCode, limit]));
  return KEY_METRIC_CODES.map((code) => buildMetric(code, byCode.get(code), languageService));
}

function buildMetric(
  code: string,
  limit: AccountUsageLimitDto | undefined,
  ls: LanguageService
): HeroMetricView {
  const label = translateWithFallback(ls, `usage.limit.${code}`, code.replace(/_/g, ' '));
  if (!limit) {
    return emptyMetric(code, label);
  }
  const state = resolveState(limit);
  const hasData = state !== 'missing' && state !== 'unavailable';
  const displayPercent = computePercent(limit, state);
  return {
    limitCode: code,
    label,
    state,
    hasData,
    consumedDisplay: hasData ? formatNumber(limit.consumedQuantity, ls) : '—',
    limitValueDisplay: limitValueDisplay(limit, ls),
    unitLabel: unitLabel(limit, ls),
    periodLabel: periodLabel(limit, ls),
    displayPercent,
    progressWidth: displayPercent ?? 0,
  };
}

function emptyMetric(code: string, label: string): HeroMetricView {
  return {
    limitCode: code,
    label,
    state: 'missing',
    hasData: false,
    consumedDisplay: '—',
    limitValueDisplay: '—',
    unitLabel: '',
    periodLabel: '',
    displayPercent: null,
    progressWidth: 0,
  };
}

/** Mismo criterio de fiabilidad que UsageLimitCardComponent. */
function hasUnavailableFlag(limit: AccountUsageLimitDto): boolean {
  const flags = limit.dataQuality ?? [];
  return flags.includes('source_unavailable') || flags.includes('usage_not_evaluated');
}

function resolveState(limit: AccountUsageLimitDto): HeroMetricState {
  if (hasUnavailableFlag(limit)) return 'unavailable';
  if (limit.limitValue == null) return 'unlimited';
  if (limit.overLimit && !limit.overageAllowed) return 'blocking';
  if (limit.overLimit && limit.overageAllowed) return 'overage_allowed';
  if ((limit.usagePercent ?? 0) >= 80) return 'warning';
  return 'healthy';
}

function computePercent(limit: AccountUsageLimitDto, state: HeroMetricState): number | null {
  if (state === 'unlimited' || state === 'unavailable' || state === 'missing') return null;
  const pct = limit.usagePercent;
  if (pct == null || !Number.isFinite(pct)) return null;
  return Math.max(0, Math.min(100, pct));
}

function limitValueDisplay(limit: AccountUsageLimitDto, ls: LanguageService): string {
  if (limit.limitValue == null) return translateWithFallback(ls, 'usage.unlimited', 'usage.unlimited');
  return formatNumber(limit.limitValue, ls);
}

function unitLabel(limit: AccountUsageLimitDto, ls: LanguageService): string {
  if (!limit.unit) return '';
  const specific = translateWithFallback(ls, `usage.unit.${limit.limitCode}`, '');
  if (specific) return specific;
  return translateWithFallback(ls, `usage.unit.${limit.unit}`, limit.unit);
}

function periodLabel(limit: AccountUsageLimitDto, ls: LanguageService): string {
  if (!limit.period) return '';
  return translateWithFallback(ls, `usage.period.${limit.period}`, '');
}

function formatNumber(value: number, ls: LanguageService): string {
  if (!Number.isFinite(value)) return '—';
  try {
    return value.toLocaleString(LOCALE_MAP[ls.getCurrentLang()] || 'es-CO');
  } catch {
    return String(value);
  }
}

function translateWithFallback(ls: LanguageService, key: string, fallback: string): string {
  const translated = ls.translate(key);
  return translated && translated !== key ? translated : fallback;
}
